/**
 * PhoneInput.jsx — shared 10-digit phone input with a static country-code
 * prefix. Default country is India (+91). Used by every action block that
 * collects a phone number so the UX is consistent and mobile keyboards
 * open in numeric mode.
 *
 * v96 (Feb 2026) — replaces bare `<input type="tel" placeholder="+1 …" />`
 * fields that were misleading users into typing US-formatted numbers.
 *
 * Behaviour:
 *   - Left prefix "+91" is visually static (a `<span>`), not editable —
 *     no country-picker in this MVP (add later if we go multi-country).
 *   - The value bubbled up to the parent is the FULL E.164 form
 *     "+91XXXXXXXXXX" so backend leads / OTP flows work unchanged.
 *   - `required` sets the HTML5 required attribute and the aria label
 *     drops the "(optional)" suffix.
 *   - Accepts only digits, max 10 digits (India NSN length).
 */
import { forwardRef } from "react";

const DEFAULT_CC = "+91";

/**
 * Split an E.164-ish string back into (cc, national) so a controlled
 * form component can pre-fill correctly. Never returns null — falls
 * back to (+91, "") if the input is unparseable.
 */
export function splitPhone(value, fallbackCC = DEFAULT_CC) {
  const v = (value || "").trim();
  if (!v) return { cc: fallbackCC, national: "" };
  // Fast path: value starts with the expected country code — just strip it.
  // Previously we used the regex /^\+(\d{1,3})(\d+)$/ which is *greedy* on
  // `\d{1,3}` and would swallow "91X" (3 digits) as the country code as
  // soon as the user typed the 3rd character, silently eating one digit
  // from the national number on every re-parse. Since PhoneInput is
  // single-country (India +91) today, prefix-strip is both simpler and
  // bug-free. When multi-country lands we swap this for a country picker.
  if (v.startsWith(fallbackCC)) {
    const rest = v.slice(fallbackCC.length).replace(/\D/g, "");
    return { cc: fallbackCC, national: rest.slice(0, 10) };
  }
  // Fallback: unknown / different country code — best-effort parse.
  const m = v.match(/^\+(\d{1,3})(\d+)$/);
  if (m) return { cc: `+${m[1]}`, national: m[2].slice(0, 10) };
  const digits = v.replace(/\D/g, "");
  return { cc: fallbackCC, national: digits.slice(-10) };
}

const PhoneInput = forwardRef(function PhoneInput(
  {
    value,
    onChange,
    required = false,
    placeholder = "10-digit mobile number",
    disabled = false,
    countryCode = DEFAULT_CC,
    className = "",
    "data-testid": testId,
    autoComplete = "tel-national",
  },
  ref,
) {
  const { national } = splitPhone(value, countryCode);

  const handleChange = (e) => {
    // Only allow digits and cap at 10
    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
    // Always emit full E.164 upstream (empty string if no digits)
    onChange(digits ? `${countryCode}${digits}` : "");
  };

  return (
    <div
      className={`flex items-stretch border-2 border-ink shadow-brutalSm bg-white focus-within:ring-2 focus-within:ring-ink/30 ${className}`}
      data-testid={testId ? `${testId}-wrapper` : undefined}
    >
      <span
        className="px-2 py-1.5 text-sm font-mono font-bold bg-ink/5 border-r-2 border-ink text-ink/80 select-none"
        aria-label={`Country code ${countryCode}`}
      >
        {countryCode}
      </span>
      <input
        ref={ref}
        type="tel"
        inputMode="numeric"
        pattern="[0-9]{10}"
        maxLength={10}
        value={national}
        onChange={handleChange}
        required={required}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete={autoComplete}
        aria-label={required ? "Mobile number (required)" : "Mobile number (optional)"}
        data-testid={testId}
        className="flex-1 min-w-0 px-2 py-1.5 text-sm bg-transparent outline-none disabled:opacity-60"
      />
    </div>
  );
});

export default PhoneInput;
